<template>
  <div class="promotions-list">
    <TotalInfoBlock v-if="paginationData?.total" :total="parseInt(paginationData?.total)" />

    <template v-if="isMobileDevice">
      <div v-if="items.length" class="flex flex-col gap-3">
        <div
          v-for="item of items"
          :key="item.id"
          class="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          @click="onClickPromotion(item.id)"
        >
          <div class="text-sm text-gray-500">{{ $t("page.users.id") }}: {{ item.id }}</div>
          <div class="mt-1 font-medium">{{ item.name }}</div>
          <div class="mt-2 text-sm text-gray-600">
            {{ $t("promotions.createdAt") }}: {{ item.createdAt }}
          </div>
          <div class="mt-1 text-sm text-gray-600">
            {{ $t("promotions.validityPeriod") }}: {{ item.validityPeriod }}
          </div>
          <OurTag class="mt-2" size="md" :text="item.status?.text" :color="item.status?.color" />
          <div
            v-if="havePermissionEdit || havePermissionDelete"
            class="mt-3 flex justify-end gap-2"
            @click.stop
          >
            <OurSvgIcon
              v-if="havePermissionDelete"
              interactive
              :src="icons.Trash"
              color="gray"
              variant="light"
              :tooltip="$t('button.delete')"
              @click="onClickDelete(item.id)"
            />
            <OurSvgIcon
              v-if="havePermissionEdit"
              interactive
              :src="icons.Edit"
              color="gray"
              variant="light"
              :tooltip="$t('button.edit')"
              @click="onClickEdit(item.id)"
            />
          </div>
        </div>
      </div>
      <SkeletonLoaderCard v-else :table-headers="resolvedTableHeaders" />
    </template>

    <OurTable
      v-else
      :headers="resolvedTableHeaders"
      :items="items"
      :table-fixed="false"
      class="promotions-list-table"
    >
      <template #cell-status="{ value }">
        <OurTag size="md" :text="value?.text" :color="value?.color" />
      </template>

      <template #cell-buttons="{ value }">
        <div
          v-if="havePermissionEdit || havePermissionDelete"
          class="flex items-center justify-end gap-2"
          data-table-action
          @click.stop
        >
          <OurSvgIcon
            v-if="havePermissionDelete"
            interactive
            :src="icons.Trash"
            color="gray"
            size="sm"
            variant="light"
            :tooltip="$t('button.delete')"
            @click.stop="onClickDelete(value)"
          />
          <OurSvgIcon
            v-if="havePermissionEdit"
            interactive
            :src="icons.Edit"
            color="gray"
            size="sm"
            variant="light"
            :tooltip="$t('button.edit')"
            @click.stop="onClickEdit(value)"
          />
        </div>
      </template>
    </OurTable>

    <OurPagination
      v-if="isMoreThanOnePage"
      :current-page="paginationData.currentPage"
      :per-page="paginationData.perPage"
      :total="paginationData.total"
      :disabled="isLoading"
      @pageChange="onPageChange"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SkeletonLoaderCard from "@/ui/loader/SkeletonLoaderCard.vue";
import TotalInfoBlock from "@/components/TotalInfoBlock.vue";
import { buildPromotionsListTableHeaders } from "@/views/settings/promotions/index/config/promotionsListTableHeaders";

export default {
  name: "PromotionsList",

  components: { SkeletonLoaderCard, TotalInfoBlock },

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    paginationData: {
      type: Object,
      default: () => ({}),
    },

    havePermissionEdit: {
      type: Boolean,
      default: false,
    },

    havePermissionDelete: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    headers: {
      type: Array,
      default: null,
    },
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),

    icons: () => ({
      Edit: require("@/components/_bit/IconSvg/icons/Edit.svg"),
      Trash: require("@/components/_bit/IconSvg/icons/Trash.svg"),
    }),

    showActionsColumn() {
      return this.havePermissionEdit || this.havePermissionDelete;
    },

    resolvedTableHeaders() {
      if (this.headers) {
        return this.headers;
      }

      return buildPromotionsListTableHeaders({
        t: (key) => this.$t(key),
        showActionsColumn: this.showActionsColumn,
      });
    },

    isMoreThanOnePage() {
      return this.paginationData.lastPage > 1;
    },

    routePage() {
      return Number.parseInt(this.$route.query.page, 10) || 1;
    },
  },

  watch: {
    routePage(newPage) {
      if (newPage !== this.paginationData.currentPage) {
        this.$emit("pageChange", newPage);
      }
    },
  },

  methods: {
    onPageChange(page) {
      this.$emit("pageChange", page);

      if (page !== this.routePage) {
        const query = { ...this.$route.query };

        delete query.page;

        const nextQuery = page === 1 ? query : { ...query, page };

        this.$router.push({ name: this.$route.name, query: nextQuery });
      }
    },

    onClickRow(row) {
      this.onClickPromotion(row.id);
    },

    onClickPromotion(id) {
      this.$emit("clickPromotion", id);
    },

    onClickEdit(id) {
      this.$emit("clickEdit", id);
    },

    onClickDelete(id) {
      this.$emit("clickDelete", id);
    },
  },
};
</script>
